// Visão do Financeiro (PO1-3745, sprint "Visão do Financeiro"). Menu "Financeiro"
// do FLOW: as faturas do embarcador em 3 grupos (Pendente / Liberado para pagar /
// Pago), com os totais em R$, filtros de período e vencimento, "Marcar como paga"
// (uma ou em lote, só para Integrada) e o relatório em PDF A4.
//
// Reaproveita a API da Auditoria Fatura: a lista vem de GET /faturas/conferencia,
// os cards de GET /faturas/financeiro/resumo e a ação de pagar passa pelo
// ConciliacaoModal (POST /faturas/conferencia/:id/conciliar e /lote). Nada de
// escrita nova no backend.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, CardBody, Input, Spinner, Table } from "reactstrap";
import moment from "moment";

import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import { useAuth } from "../../../hooks/auth";
import { useToast } from "../../../hooks/toast";
import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import TablePagination from "../../../components/TablePagination";
import FilterBand from "../../../components/FilterBand";
import {
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotSecondaryButtonStyle,
} from "../../../components/autopilot/styles";

import { fmtBRL, fmtInt, fmtData, fmtDataPura, fmtDataBRT, labelSituacao, dataPura } from "../ConferenciaFatura/formatters";
import { ConciliacaoModal } from "../ConferenciaFatura/modais";
import { StatusBadge } from "../ConferenciaFatura/componentesCompartilhados";
import { GRUPOS, GRUPO_PADRAO, grupoPorKey, podeMarcarPaga, motivoNaoMarcavel, resumoVazio } from "./grupos";
import { baixarPdfFinanceiro, LIMITE_LINHAS_POR_GRUPO } from "./relatorioPdf";

import "./styles.css";

const DATA_CAMPOS = [
  { value: "vencimento", label: "Vencimento" },
  { value: "emissao", label: "Emissão" },
  { value: "integracao", label: "Integração" },
  { value: "pagamento", label: "Pagamento" },
];

const FILTROS_INICIAIS = {
  dataCampo: "vencimento",
  dataDe: "",
  dataAte: "",
  nrFaturaExt: "",
};

const PAGE_SIZES = [25, 50, 100];
const ACAO_PAGA = "MARCAR_PAGA";

const msgErro = (e, padrao) =>
  (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
  (e && e.message) ||
  padrao;

// Quem pode marcar como paga: embarcador ou admin Everlog (mesma regra do
// ConciliacaoModal / backend, papel EMB).
const ehEmbarcador = (user) => Boolean(user && (user.setor === "empresa" || user.setor === "admin"));

// Situações em que a fatura já saiu da conferência: Liberada e Integrada (grupo
// Liberado) e Paga. Decisão do Rodrigo no QA (02/11/2026): liberada é status
// finalizador ("já liberou, está ok"), então não leva alerta de vencimento.
const SITUACOES_SEM_ALERTA_VENCIMENTO = ["LIB", "PAG", "PGO", "CAN"];

// Vencimento com destaque, só para fatura ainda Pendente (em conferência ou com
// divergência): vencida (vermelho) ou vence em até 7 dias (âmbar). Olha a situação
// da linha, não o grupo ativo, para valer também na busca por número.
function classeVencimento(f) {
  if (!f || !f.dataVencimentoExterna) return "";
  if (SITUACOES_SEM_ALERTA_VENCIMENTO.includes(f.situacaoCodigo)) return "";
  const venc = dataPura(f.dataVencimentoExterna);
  if (!venc) return "";
  const dias = venc.diff(moment().startOf("day"), "days");
  if (dias < 0) return " is-vencida";
  if (dias <= 7) return " is-proxima";
  return "";
}

// Filtro rápido da tabela: nº externo (ou id) contém o texto digitado.
export function filtrarPorNumero(lista, texto) {
  const q = String(texto || "").trim().toLowerCase();
  if (!q) return lista;
  return (lista || []).filter((f) => String(f.numFaturaExterna || f.id || "").toLowerCase().includes(q));
}

function dataDaColuna(grupo, f) {
  const v = f[grupo.colunaData.campo];
  if (!v) return "-";
  return grupo.colunaData.formato === "dataBRT" ? fmtDataBRT(v) : fmtData(v);
}

export default function VisaoFinanceiro() {
  const { empresa, transportadoraId } = useCompanies();
  const empresaId = empresa && empresa.value;
  const empresaNome = empresa && empresa.label;
  const { user } = useAuth();
  const { addToast } = useToast();
  const podeAgir = ehEmbarcador(user);

  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [grupoKey, setGrupoKey] = useState(GRUPO_PADRAO);
  const [resumo, setResumo] = useState(resumoVazio());
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selecionadas, setSelecionadas] = useState(new Set());
  const [conciliacao, setConciliacao] = useState(null); // { fatura } | { faturas }
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  // Filtro rápido da tabela (exceção ao padrão, pedido do Rodrigo no QA de 02/11):
  // só o nº da fatura, na lista já carregada, sem nova consulta ao backend.
  const [filtroTabela, setFiltroTabela] = useState("");

  const grupo = grupoPorKey(grupoKey) || GRUPOS[1];
  const buscaPorNumero = Boolean(filtros.nrFaturaExt && filtros.nrFaturaExt.trim());

  // Filtros comuns à lista e ao resumo. Sem data na abertura: o financeiro vê
  // tudo o que está em aberto, inclusive vencidas antigas.
  const paramsBase = useCallback(
    (f = filtros) => {
      const params = { mostrarFinalizadas: 1 };
      if (f.dataCampo && (f.dataDe || f.dataAte)) {
        params.dataCampo = f.dataCampo;
        if (f.dataDe) params.dataDe = f.dataDe;
        if (f.dataAte) params.dataAte = f.dataAte;
      }
      if (f.nrFaturaExt && f.nrFaturaExt.trim()) params.nrFaturaExt = f.nrFaturaExt.trim();
      if (transportadoraId) params.idTransportadora = transportadoraId;
      return params;
    },
    [filtros, transportadoraId]
  );

  const validarFiltros = useCallback(() => {
    if (filtros.dataDe && filtros.dataAte && filtros.dataDe > filtros.dataAte) {
      return "A data inicial não pode ser maior que a data final.";
    }
    return null;
  }, [filtros]);

  const buscarResumo = useCallback(async (filtrosArg) => {
    try {
      const res = await api.get("/faturas/financeiro/resumo", { params: paramsBase(filtrosArg || filtros) });
      setResumo({ ...resumoVazio(), ...((res.data && res.data.grupos) || {}) });
    } catch (e) {
      setResumo(resumoVazio());
    }
  }, [paramsBase, filtros]);

  // `filtrosArg` permite buscar com um valor que acabou de mudar (busca da faixa),
  // antes de o estado `filtros` ser atualizado.
  const buscar = useCallback(
    async (pagina, tamanho, grupoArg, filtrosArg) => {
      const f = filtrosArg || filtros;
      const porNumero = Boolean(f.nrFaturaExt && f.nrFaturaExt.trim());
      const g = grupoPorKey(grupoArg || grupoKey) || grupo;
      setLoading(true);
      setError(null);
      try {
        const params = {
          ...paramsBase(f),
          page: pagina,
          pageSize: tamanho,
          sortField: "dataVencimentoExterna",
          sortDir: "asc",
        };
        // Busca por número acha a fatura em qualquer grupo (como na Auditoria).
        if (!porNumero) params.idSituacoes = g.idSituacoes.join(",");
        const res = await api.get("/faturas/conferencia", { params });
        setItems(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
      } catch (e) {
        setItems([]);
        setTotalCount(0);
        setError(msgErro(e, "Erro ao carregar faturas"));
      } finally {
        setLoading(false);
      }
    },
    [paramsBase, grupoKey, grupo, filtros]
  );

  const recarregar = useCallback(() => {
    const erro = validarFiltros();
    if (erro) {
      setError(erro);
      return;
    }
    setPage(1);
    setSelecionadas(new Set());
    buscar(1, pageSize);
    buscarResumo();
  }, [validarFiltros, buscar, buscarResumo, pageSize]);

  // Busca da faixa "Buscar fatura" (Enter ou debounce do FilterBand): dispara já
  // com o número digitado, em qualquer grupo; vazio volta para o grupo ativo.
  const buscarPorNumero = useCallback(
    (valor) => {
      const novo = { ...filtros, nrFaturaExt: valor || "" };
      setPage(1);
      setSelecionadas(new Set());
      buscar(1, pageSize, null, novo);
      buscarResumo(novo);
    },
    [filtros, buscar, buscarResumo, pageSize]
  );

  // Carrega ao abrir e quando o embarcador/transportadora do header mudam.
  useEffect(() => {
    setFiltros(FILTROS_INICIAIS);
    setGrupoKey(GRUPO_PADRAO);
    setPage(1);
    setSelecionadas(new Set());
    const params = { mostrarFinalizadas: 1 };
    if (transportadoraId) params.idTransportadora = transportadoraId;
    setLoading(true);
    Promise.all([
      api.get("/faturas/conferencia", {
        params: {
          ...params,
          idSituacoes: (grupoPorKey(GRUPO_PADRAO) || GRUPOS[1]).idSituacoes.join(","),
          page: 1,
          pageSize: 50,
          sortField: "dataVencimentoExterna",
          sortDir: "asc",
        },
      }),
      api.get("/faturas/financeiro/resumo", { params }),
    ])
      .then(([lista, res]) => {
        setItems(lista.data.items || []);
        setTotalCount(lista.data.totalCount || 0);
        setResumo({ ...resumoVazio(), ...((res.data && res.data.grupos) || {}) });
        setPageSize(50);
        setError(null);
      })
      .catch((e) => {
        setItems([]);
        setTotalCount(0);
        setResumo(resumoVazio());
        setError(msgErro(e, "Erro ao carregar faturas"));
      })
      .finally(() => setLoading(false));
  }, [empresaId, transportadoraId]);

  const selecionarGrupo = (key) => {
    if (key === grupoKey) return;
    setGrupoKey(key);
    setPage(1);
    setSelecionadas(new Set());
    buscar(1, pageSize, key);
  };

  const setFiltro = (campo, valor) => setFiltros((prev) => ({ ...prev, [campo]: valor }));

  const limparFiltros = () => {
    setFiltros(FILTROS_INICIAIS);
    setPage(1);
    setSelecionadas(new Set());
    // paramsBase lê `filtros` do closure: busca com o objeto limpo explicitamente.
    const params = { mostrarFinalizadas: 1 };
    if (transportadoraId) params.idTransportadora = transportadoraId;
    setLoading(true);
    Promise.all([
      api.get("/faturas/conferencia", {
        params: { ...params, idSituacoes: grupo.idSituacoes.join(","), page: 1, pageSize, sortField: "dataVencimentoExterna", sortDir: "asc" },
      }),
      api.get("/faturas/financeiro/resumo", { params }),
    ])
      .then(([lista, res]) => {
        setItems(lista.data.items || []);
        setTotalCount(lista.data.totalCount || 0);
        setResumo({ ...resumoVazio(), ...((res.data && res.data.grupos) || {}) });
        setError(null);
      })
      .catch((e) => setError(msgErro(e, "Erro ao carregar faturas")))
      .finally(() => setLoading(false));
  };

  const mudarPagina = (p) => {
    setPage(p);
    setSelecionadas(new Set());
    buscar(p, pageSize);
  };

  const mudarTamanho = (t) => {
    setPageSize(t);
    setPage(1);
    setSelecionadas(new Set());
    buscar(1, t);
  };

  // Seleção em lote: só Integradas (PAG), só para quem pode agir.
  const itensVisiveis = useMemo(() => filtrarPorNumero(items, filtroTabela), [items, filtroTabela]);
  // Mudou o filtro rápido: limpa a seleção, para o lote nunca levar fatura escondida.
  useEffect(() => {
    setSelecionadas(new Set());
  }, [filtroTabela]);
  const selecionaveis = useMemo(() => itensVisiveis.filter(podeMarcarPaga), [itensVisiveis]);
  const todasSelecionadas = selecionaveis.length > 0 && selecionaveis.every((f) => selecionadas.has(f.id));
  const alternarSelecao = (id) =>
    setSelecionadas((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const alternarTodas = () =>
    setSelecionadas(todasSelecionadas ? new Set() : new Set(selecionaveis.map((f) => f.id)));

  const abrirLote = () => {
    const faturas = items.filter((f) => selecionadas.has(f.id) && podeMarcarPaga(f));
    if (!faturas.length) return;
    setConciliacao({ faturas });
  };

  const aoConcluir = (resultado) => {
    const lote = Array.isArray(resultado);
    const ok = lote ? resultado.filter((r) => r && r.ok !== false && !r.erro).length : 1;
    const falhas = lote ? resultado.length - ok : 0;
    addToast({
      place: "tr",
      type: falhas ? "warning" : "success",
      title: falhas ? "Concluído com falhas" : "Pagamento registrado",
      description: lote
        ? `${fmtInt(ok)} fatura(s) marcada(s) como paga(s)${falhas ? `, ${fmtInt(falhas)} com erro` : ""}.`
        : "A fatura passou para Pago.",
      icon: falhas ? "tim-icons icon-alert-circle-exc" : "tim-icons icon-check-2",
    });
    setConciliacao(null);
    setSelecionadas(new Set());
    buscar(page, pageSize);
    buscarResumo();
  };

  const gerarPdf = async () => {
    const erro = validarFiltros();
    if (erro) {
      setError(erro);
      return;
    }
    setGerandoPdf(true);
    setError(null);
    try {
      const base = paramsBase();
      const respostas = await Promise.all(
        GRUPOS.map((g) =>
          api.get("/faturas/conferencia", {
            params: {
              ...base,
              idSituacoes: g.idSituacoes.join(","),
              page: 1,
              pageSize: LIMITE_LINHAS_POR_GRUPO,
              sortField: "dataVencimentoExterna",
              sortDir: "asc",
            },
          })
        )
      );
      const acima = GRUPOS.filter((g, i) => (respostas[i].data.totalCount || 0) > LIMITE_LINHAS_POR_GRUPO);
      if (acima.length) {
        setError(
          `O relatório sai com até ${fmtInt(LIMITE_LINHAS_POR_GRUPO)} faturas por grupo. ` +
            `${acima.map((g) => g.label).join(" e ")} passa desse limite: filtre o período (por vencimento, por exemplo) e gere de novo.`
        );
        return;
      }
      const porGrupo = {};
      GRUPOS.forEach((g, i) => {
        porGrupo[g.key] = respostas[i].data.items || [];
      });
      await baixarPdfFinanceiro({
        embarcador: empresaNome,
        filtros: { ...filtros, transportadoraNome: null },
        porGrupo,
      });
    } catch (e) {
      setError(msgErro(e, "Não foi possível gerar o relatório"));
    } finally {
      setGerandoPdf(false);
    }
  };

  const mostrarSelecao = podeAgir && grupoKey === "LIBERADO" && !buscaPorNumero;
  const filtrosAtivos = filtros.dataDe || filtros.dataAte ? 1 : 0;

  return (
    <div className="content conf-fatura-page vfin-page">
      <PageHero
        eyebrow="Flow · Financeiro"
        title="Financeiro"
        description="O que ainda está em conferência, o que já pode ser pago e o que já foi pago. Marque o pagamento e imprima o relatório."
        actions={
          <>
            <Button color="secondary" className="btn-sm" outline onClick={gerarPdf} disabled={gerandoPdf || loading}>
              {gerandoPdf ? <Spinner size="sm" /> : "Relatório PDF"}
            </Button>
            <PageHeroRefreshButton onClick={recarregar} loading={loading} />
          </>
        }
      />

      {/* Mesmo padrão da Auditoria Fatura (ConferenciaFatura/index.js): um card com a
          faixa "Buscar fatura" + botão Filtros (FilterBand); o painel de filtros fica
          escondido até o clique e o Status (cards dos grupos) vem em seção própria. */}
      <Card
        className="card-everlog-shell"
        style={{ ...autopilotCardStyle, marginTop: "1.25rem", background: "var(--everlog-card-hero-bg)" }}
      >
        <CardBody style={autopilotBodyStyle}>
          <div className="conf-fatura-control-panel">
            <FilterBand
              mode="server"
              withCard={false}
              searchLabel="Buscar fatura"
              searchHint="Nº externo, em qualquer grupo"
              searchPlaceholder="Digite o nº da fatura"
              searchType="text"
              searchValue={filtros.nrFaturaExt}
              onSearchChange={(valor) => setFiltro("nrFaturaExt", valor)}
              onSearchSubmit={buscarPorNumero}
              activeFiltersCount={filtrosAtivos}
              open={mostrarFiltros}
              onToggle={setMostrarFiltros}
              showFiltersButton
              style={{ "--hub-fb-label-max": "210px" }}
            />

            <div className="conf-fatura-control-grid">
              <section className="conf-fatura-control-section conf-fatura-control-section--status">
                <div className="conf-fatura-control-section__head">
                  <div>
                    <div className="conf-fatura-control-section__title">Status</div>
                    <div className="conf-fatura-control-section__subtitle">
                      Selecione um grupo para ver as faturas dele. Os valores são o total cobrado nas faturas.
                    </div>
                  </div>
                </div>
                <div className="conf-fatura-status-grid" role="tablist" aria-label="Grupos de faturas">
                  {GRUPOS.map((g) => {
                    const r = resumo[g.key] || { qtd: 0, valor: 0 };
                    const ativo = g.key === grupoKey;
                    return (
                      <button
                        key={g.key}
                        type="button"
                        role="tab"
                        aria-selected={ativo}
                        className={`conf-fatura-status-card${ativo ? " is-active" : ""}`}
                        style={{ "--conf-fatura-status-color": g.cor }}
                        onClick={() => selecionarGrupo(g.key)}
                        disabled={loading}
                        title={g.descricao}
                        data-grupo={g.key}
                      >
                        <span className="conf-fatura-status-card__dot" style={{ background: g.cor }} />
                        <span className="conf-fatura-status-card__copy">
                          <span className="conf-fatura-status-card__label">{g.label}</span>
                          <span className="conf-fatura-status-card__count">{fmtBRL(r.valor)}</span>
                          <span className="vfin-status-card__qtd">{fmtInt(r.qtd)} fatura(s)</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section
                className="conf-fatura-control-section conf-fatura-control-section--filters"
                style={{ display: mostrarFiltros ? undefined : "none" }}
              >
                <div className="conf-fatura-control-section__head">
                  <div>
                    <div className="conf-fatura-control-section__title">Filtros</div>
                    <div className="conf-fatura-control-section__subtitle">
                      {filtrosAtivos > 0 ? `${filtrosAtivos} filtro(s) avançado(s) ativo(s)` : "Sem filtros avançados ativos"}
                    </div>
                  </div>
                </div>

                <div className="conf-fatura-advfilters__body">
                  <div className="conf-fatura-filter-layout">
                    <div className="conf-fatura-filter-group conf-fatura-filter-group--date">
                      <div className="conf-fatura-filter-group__title">Data</div>
                      <div className="conf-fatura-filter-field">
                        <span className="conf-fatura-filter-label">Tipo</span>
                        <div className="conf-fatura-filter-datas__pills">
                          {DATA_CAMPOS.map((d) => (
                            <button
                              key={d.value}
                              type="button"
                              className={`conf-fatura-pill${filtros.dataCampo === d.value ? " is-active" : ""}`}
                              onClick={() => setFiltro("dataCampo", d.value)}
                              disabled={loading}
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="conf-fatura-filter-datas__range">
                        <div className="conf-fatura-filter-field">
                          <label className="conf-fatura-filter-label" htmlFor="vfin-data-de">De</label>
                          <Input id="vfin-data-de" type="date" className="conf-fatura-filter-input" value={filtros.dataDe} onChange={(e) => setFiltro("dataDe", e.target.value)} disabled={loading} />
                        </div>
                        <div className="conf-fatura-filter-field">
                          <label className="conf-fatura-filter-label" htmlFor="vfin-data-ate">Até</label>
                          <Input id="vfin-data-ate" type="date" className="conf-fatura-filter-input" value={filtros.dataAte} onChange={(e) => setFiltro("dataAte", e.target.value)} disabled={loading} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="conf-fatura-filter-actions">
                    <Button size="sm" style={autopilotSecondaryButtonStyle} onClick={recarregar} disabled={loading}>
                      {loading ? <Spinner size="sm" /> : "Aplicar filtros"}
                    </Button>
                    <Button size="sm" color="default" outline onClick={limparFiltros} disabled={loading}>
                      Limpar
                    </Button>
                  </div>
                </div>
              </section>
            </div>
            {error ? <div className="vfin-erro" role="alert">{error}</div> : null}
          </div>
        </CardBody>
      </Card>

      <ShellCard
        eyebrow={buscaPorNumero ? "Busca por número" : grupo.label}
        title={buscaPorNumero ? `Fatura ${filtros.nrFaturaExt.trim()}` : `Faturas em ${grupo.label.toLowerCase()}`}
        description={buscaPorNumero ? "Resultado em qualquer grupo." : grupo.descricao}
        action={
          <div className="vfin-tabela-acoes">
            <Input
              id="vfin-filtro-tabela"
              type="search"
              bsSize="sm"
              className="vfin-filtro-tabela"
              placeholder="Filtrar nº da fatura"
              aria-label="Filtrar as faturas desta lista pelo número"
              value={filtroTabela}
              onChange={(e) => setFiltroTabela(e.target.value)}
            />
            <span className="text-muted">
              {filtroTabela.trim()
                ? `${fmtInt(itensVisiveis.length)} de ${fmtInt(items.length)} nesta página`
                : `${fmtInt(totalCount)} fatura(s)`}
            </span>
          </div>
        }
      >
        <TableSurface>
          <Table size="sm" responsive hover className="hub-thead-accent vfin-tabela">
            <thead className="text-primary">
              <tr>
                {mostrarSelecao ? (
                  <th style={{ width: 36 }} className="text-center">
                    <input
                      type="checkbox"
                      className="conf-fatura-sel"
                      aria-label="Selecionar todas as faturas integradas da página"
                      checked={todasSelecionadas}
                      disabled={selecionaveis.length === 0}
                      onChange={alternarTodas}
                    />
                  </th>
                ) : null}
                <th>Fatura</th>
                <th>Transportadora</th>
                <th>Emissão</th>
                <th>Vencimento</th>
                <th>Situação</th>
                <th>{buscaPorNumero ? "Liberada / paga em" : grupo.colunaData.label}</th>
                <th className="vfin-num">Valor</th>
                {podeAgir ? <th>Ação</th> : null}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="vfin-estado">
                    <Spinner size="sm" /> Carregando...
                  </td>
                </tr>
              ) : itensVisiveis.length === 0 ? (
                <tr>
                  <td colSpan={9} className="vfin-estado">
                    {error
                      ? "Não foi possível carregar as faturas."
                      : items.length > 0
                      ? `Nenhuma fatura desta página tem "${filtroTabela.trim()}" no número.`
                      : "Nenhuma fatura neste grupo com os filtros atuais."}
                  </td>
                </tr>
              ) : (
                itensVisiveis.map((f) => {
                  const marcavel = podeMarcarPaga(f);
                  const gLinha = buscaPorNumero ? grupoPorKey(["PGO"].includes(f.situacaoCodigo) ? "PAGO" : "LIBERADO") : grupo;
                  return (
                    <tr key={f.id} data-situacao={f.situacaoCodigo}>
                      {mostrarSelecao ? (
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="conf-fatura-sel"
                            aria-label={`Selecionar fatura ${f.numFaturaExterna || f.id}`}
                            checked={selecionadas.has(f.id)}
                            disabled={!marcavel}
                            title={marcavel ? undefined : motivoNaoMarcavel(f)}
                            onChange={() => alternarSelecao(f.id)}
                          />
                        </td>
                      ) : null}
                      <td>{f.numFaturaExterna || f.id}</td>
                      <td>{f.transportadoraFantasia || "-"}</td>
                      <td>{fmtData(f.dataEmissaoExterna) || "-"}</td>
                      <td>
                        <span className={`vfin-vencimento${classeVencimento(f)}`}>
                          {fmtDataPura(f.dataVencimentoExterna) || "-"}
                        </span>
                      </td>
                      <td>
                        <StatusBadge codigo={f.situacaoCodigo}>{labelSituacao(f.situacaoCodigo, f.situacaoNome)}</StatusBadge>
                      </td>
                      <td>{dataDaColuna(gLinha, f)}</td>
                      <td className="vfin-num">{fmtBRL(f.valTotalFatura)}</td>
                      {podeAgir ? (
                        <td>
                          {marcavel ? (
                            <Button color="success" size="sm" outline onClick={() => setConciliacao({ fatura: f })} data-acao="marcar-paga">
                              Marcar como paga
                            </Button>
                          ) : (
                            <span className="vfin-acao-indisponivel" title={motivoNaoMarcavel(f)}>
                              {f.situacaoCodigo === "PGO" ? "Paga" : "Sem ação aqui"}
                            </span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
          <TablePagination
            page={page}
            pageSize={pageSize}
            total={totalCount}
            onPageChange={mudarPagina}
            onPageSizeChange={mudarTamanho}
            pageSizes={PAGE_SIZES}
            disabled={loading}
            itemLabel="fatura(s)"
          />
        </TableSurface>

        {mostrarSelecao && selecionadas.size > 0 ? (
          <div className="vfin-lote" role="region" aria-label="Ações nas faturas selecionadas">
            <span>
              <b>{fmtInt(selecionadas.size)}</b> fatura(s) selecionada(s)
            </span>
            <Button color="success" size="sm" onClick={abrirLote} data-lote="marcar-paga">
              Marcar como paga ({fmtInt(selecionadas.size)})
            </Button>
            <span className="vfin-lote__espaco" />
            <Button color="secondary" size="sm" outline onClick={() => setSelecionadas(new Set())}>
              Limpar seleção
            </Button>
          </div>
        ) : null}
      </ShellCard>

      <ConciliacaoModal
        isOpen={conciliacao !== null}
        onClose={() => setConciliacao(null)}
        fatura={conciliacao && conciliacao.fatura}
        faturas={conciliacao && conciliacao.faturas}
        acao={conciliacao ? ACAO_PAGA : null}
        onDone={aoConcluir}
      />
    </div>
  );
}
